import { GitCompare, type LucideIcon } from "lucide-react"

/**
 * Route-keyed illustrated tool icons for the /tools index glass cards.
 *
 * These SVGs are copied from the homepage tools-chip strip
 * (components/home/tools-chip-strip.tsx) so the /tools grid uses the same
 * artwork. They are intentionally duplicated rather than imported: the chip
 * strip mixes them with homepage-only chrome (the /diagnose entry, the
 * "Most used" badge, PostHog ordering), so a shared module that BOTH consume
 * is a separate, independently-regression-checkable refactor. Keep the two in
 * sync when an icon changes; consolidation is tracked as a follow-up.
 *
 * Any active tool route without an illustration here (e.g. grass-comparison)
 * falls back to its manifest lucide glyph via FALLBACK_ICONS.
 */

const G = "#16a34a" // green-600
const G_DARK = "#15803d" // green-700
const G_LIGHT = "#86efac" // green-300
const SOIL = "#92400e" // amber-800
const SUN = "#fbbf24" // amber-400
const WATER = "#3b82f6" // blue-500
const RED = "#ef4444"
const GRAY = "#9ca3af"
const INK = "#0f1d14"

/**
 * Shared fertilizer-bag silhouette for the product-calculator family
 * (Milorganite, the Scotts line, PGF, Lesco, Ironite, etc.). Each product reads
 * as its own card via its real NPK on the label band + a small identity badge,
 * while staying one visual set. NPK values come from data/fertilizer-products.ts
 * — keep them truthful, not decorative.
 */
function FertBag({
  band,
  label,
  badge,
  labelFill = "#fff",
  fontSize = 3.4,
}: {
  band: string
  label: string
  badge?: React.ReactNode
  labelFill?: string
  fontSize?: number
}) {
  return (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* grass tufts + ground line, shared across the family */}
      <path d="M4 31c0-3 1-5 2-6M7 31c0-4 1-6 2-7M27 31c0-3 1-5 2-6M30 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      {/* bag body + folded top */}
      <path d="M10 12l1.6-4h12.8L26 12v15.4a2.6 2.6 0 01-2.6 2.6H12.6A2.6 2.6 0 0110 27.4z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M13 8l1-1h8l1 1" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      {/* NPK label band */}
      <rect x="11.4" y="14.4" width="13.2" height="5.6" rx="1.2" fill={band} />
      <text x="18" y="18.5" textAnchor="middle" fontSize={fontSize} fontWeight="800" fill={labelFill} fontFamily="ui-sans-serif, system-ui">{label}</text>
      {badge}
    </svg>
  )
}

// viewBox 0 0 36 36, rendered at 38px inside the card's 56px tile.
const TOOL_SVG: Record<string, React.ReactNode> = {
  "/tools/soil-temperature": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <circle cx="29" cy="7" r="2.4" fill={SUN} />
      <path d="M29 2v2M29 10v1M24 7h2M32 7h2M25.6 3.6l1.2 1.2M31.2 9.2l1.2 1.2M25.6 10.4l1.2-1.2M31.2 4.8l1.2-1.2" stroke={SUN} strokeWidth="1" strokeLinecap="round" />
      <path d="M2 28h32" stroke={SOIL} strokeWidth="3.5" strokeLinecap="round" opacity="0.18" />
      <rect x="14" y="5" width="5" height="19" rx="2.5" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <rect x="15.2" y="8" width="2.6" height="14" rx="1.3" fill="#fee2e2" />
      <rect x="15.2" y="15" width="2.6" height="7" rx="1.3" fill={RED} />
      <circle cx="16.5" cy="26" r="3.6" stroke={INK} strokeWidth="1.6" fill={RED} />
      <path d="M11.5 9h1.4M11.5 13h1.4M11.5 17h1.4M11.5 21h1.4" stroke={INK} strokeWidth="0.9" strokeLinecap="round" />
      <path d="M4 31c0-3 1-5 2-6M7 31c0-4 1-6 2-7M24 31c0-3 1-5 2-6M27 31c0-5 1-7 2-8M31 31c0-3 1-5 2-6" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/herbicide-timing": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect x="5" y="8" width="22" height="20" rx="2.4" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <rect x="5" y="8" width="22" height="5" rx="2.4" fill={G} />
      <path d="M10 6v4M22 6v4" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M9 17h2M14 17h2M19 17h2M24 17h2M9 21h2M19 21h2M24 21h2" stroke={GRAY} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="15" cy="21" r="2.8" fill={G} />
      <circle cx="15" cy="21" r="2.8" stroke={G_DARK} strokeWidth="0.8" fill="none" />
      <path d="M13.4 21l1.1 1.1 2.1-2.1" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="29" cy="29" r="3.6" fill="#fff" stroke={G_DARK} strokeWidth="1.4" />
      <path d="M29 27v2.4l1.6 1" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/fertilizer-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M11 11l1.5-3.5h11L25 11v17a2.5 2.5 0 01-2.5 2.5h-9A2.5 2.5 0 0111 28z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <rect x="13.5" y="14.5" width="9" height="6" rx="1" fill={G} />
      <text x="18" y="19" textAnchor="middle" fontSize="3.8" fontWeight="800" fill="#fff" fontFamily="ui-sans-serif, system-ui">NPK</text>
      <path d="M14 7.5l1-1h6l1 1" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="14" cy="25" r="0.9" fill={G} />
      <circle cx="17" cy="26" r="0.9" fill={G_LIGHT} />
      <circle cx="20" cy="25" r="0.9" fill={G} />
      <circle cx="22" cy="27" r="0.9" fill={G_LIGHT} />
      <circle cx="13" cy="28" r="0.7" fill={G_LIGHT} />
      <path d="M5 33c1-3 2-4 4-4M7 33c0-3 1-5 2-6M27 33c1-3 3-4 4-4M30 33c0-3 1-5 2-6" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 34h32" stroke={INK} strokeWidth="1.3" strokeLinecap="round" opacity="0.7" />
    </svg>
  ),
  "/tools/lawn-size-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M5 8l8-2 12 4 6-1v18l-7 2-11-3-8 1z" stroke={G_DARK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M5 8v18M13 6v25M25 10v22M31 9v18" stroke={G_LIGHT} strokeWidth="0.8" strokeDasharray="1.5 1.5" strokeLinecap="round" />
      <circle cx="18" cy="17" r="2.2" fill={G} />
      <circle cx="18" cy="17" r="3.6" stroke={G} strokeWidth="1.2" fill="none" opacity="0.5" />
      <path d="M6 30h24" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6 28v4M30 28v4M12 29v2M18 29v2M24 29v2" stroke={INK} strokeWidth="1.2" strokeLinecap="round" />
      <text x="18" y="34.5" textAnchor="middle" fontSize="3.6" fontWeight="700" fill={INK} fontFamily="ui-sans-serif, system-ui">SQ FT</text>
    </svg>
  ),
  "/tools/seeding-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M9 5l11-2.5 1.5 7-10 2z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M11 11l9-2" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M14 13.5l-1.5 4M17 14l-1 4.5M19.5 13.5l1 5M22 13l2 4" stroke={SOIL} strokeWidth="1" strokeLinecap="round" opacity="0.5" />
      <circle cx="13" cy="20" r="0.9" fill={SOIL} />
      <circle cx="16" cy="22" r="0.9" fill={SOIL} />
      <circle cx="19" cy="20" r="0.9" fill={SOIL} />
      <circle cx="22" cy="23" r="0.9" fill={SOIL} />
      <circle cx="11" cy="24" r="0.7" fill={SOIL} />
      <circle cx="24" cy="20" r="0.7" fill={SOIL} />
      <path d="M14 30c0-3 0-5-1-7M17 30c0-2 0-3.5-.5-5M20 30c0-3 0-5 1-7M23 30c0-2 0-3.5.5-5M11 30c0-2 0-3 0-4M26 30c0-2 0-3 0-4" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M3 32h30" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/watering-schedule": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M9 13h11v9a3 3 0 01-3 3h-5a3 3 0 01-3-3z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M11.5 13v-2a3 3 0 013-3h2" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M20 16l5-2v3l-3 1" stroke={INK} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M22 18.5c-.5 1-1 2-1 3" stroke={WATER} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M25 19c-1 1-2 2-2 3.5c0 1 .8 1.5 1.5 1.5s1.5-.5 1.5-1.5c0-1.5-1-2.5-1-3.5z" fill={WATER} />
      <path d="M28 23c-1 1-1.5 2-1.5 3c0 .8.6 1.2 1.2 1.2s1.2-.4 1.2-1.2c0-1-.9-2-.9-3z" fill={WATER} opacity="0.7" />
      <path d="M19 31c0-3 1-5 2-7M22 31c0-2 1-4 2-5M26 31c0-3 1-5 2-7M30 31c0-2 1-4 2-5M16 31c0-2 0-3 0-4M13 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M2 32.5h32" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  "/tools/mowing-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M28 6l-7 13" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M26 5h5" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <rect x="6" y="18" width="17" height="8" rx="2" stroke={INK} strokeWidth="1.6" fill={G_LIGHT} />
      <path d="M9 18v-3M14 18v-3M19 18v-3" stroke={G_DARK} strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="11" cy="28" r="2.6" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <circle cx="11" cy="28" r="0.9" fill={INK} />
      <circle cx="20" cy="28" r="2.6" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <circle cx="20" cy="28" r="0.9" fill={INK} />
      <path d="M3 31c1-1 2-1 3 0" stroke={G} strokeWidth="1.3" strokeLinecap="round" fill="none" />
      <path d="M27 31c0-3 1-5 2-7M30 31c0-2 1-4 2-5M33 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M2 33h32" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  "/tools/soil-test-analyzer": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M2 28h32" stroke={SOIL} strokeWidth="3.5" strokeLinecap="round" opacity="0.18" />
      <path d="M9 4h13l4 4v20a1.5 1.5 0 01-1.5 1.5H9.5A1.5 1.5 0 018 28V5.5A1.5 1.5 0 019 4z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M22 4v4h4" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" fill="#fff" />
      <rect x="10.5" y="10" width="9" height="3.5" rx="0.8" fill={INK} opacity="0.85" />
      <text x="15" y="12.7" textAnchor="middle" fontSize="2.8" fontWeight="800" fill="#fff" fontFamily="ui-sans-serif, system-ui">PDF</text>
      <line x1="10.5" y1="17" x2="22" y2="17" stroke={GRAY} strokeWidth="1.2" strokeLinecap="round" />
      <line x1="10.5" y1="20" x2="20" y2="20" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
      <line x1="10.5" y1="23" x2="21" y2="23" stroke={RED} strokeWidth="1.4" strokeLinecap="round" />
      <line x1="10.5" y1="26" x2="18" y2="26" stroke={GRAY} strokeWidth="1.2" strokeLinecap="round" />
      <path d="M29 11l0.6 1.6 1.6 0.6-1.6 0.6-0.6 1.6-0.6-1.6-1.6-0.6 1.6-0.6z" fill={SUN} stroke={INK} strokeWidth="0.6" strokeLinejoin="round" />
      <path d="M3 31c0-2 0.5-3 1-4M5 31c0-3 0.5-4 1-5M30 31c0-2 0.5-3 1-4M32 31c0-3 0.5-4 1-5" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/aeration-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M5 14c0-3 1-5 2-6M10 14c0-4 1-6 2-7M16 14c0-3 1-5 2-6M22 14c0-4 1-6 2-7M28 14c0-3 1-5 2-6" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M3 14h30" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M3 14h30v14a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke={INK} strokeWidth="1.6" fill="#fef3c7" strokeLinejoin="round" opacity="0.5" />
      <ellipse cx="10" cy="20" rx="1.8" ry="1.2" fill={INK} />
      <ellipse cx="18" cy="20" rx="1.8" ry="1.2" fill={INK} />
      <ellipse cx="26" cy="20" rx="1.8" ry="1.2" fill={INK} />
      <ellipse cx="14" cy="26" rx="1.8" ry="1.2" fill={INK} />
      <ellipse cx="22" cy="26" rx="1.8" ry="1.2" fill={INK} />
    </svg>
  ),
  "/tools/dethatching-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Spring-tine rake pulling the brown thatch layer out of the turf
          cross-section: green blades on top, thatch band, soil below. */}
      <path d="M31 4L21 15" stroke={INK} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M29 2.5l4 3.5" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M15 15h8" stroke={INK} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M15.5 15c-.6 2-1.6 3.2-2.8 4M18 15c-.4 2-1.2 3.4-2.2 4.4M20.5 15c-.3 2-1 3.5-1.8 4.6M23 15c-.2 2-.7 3.6-1.4 4.8" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M4 24c1.2-1.4 2.8-1.8 4.2-1M7 25.5c1.4-1.2 3-1.4 4.4-.6M10.5 23.5c1.2-1 2.6-1.2 3.8-.5" stroke={SOIL} strokeWidth="1.4" strokeLinecap="round" opacity="0.85" />
      <path d="M3 20c1-2 2-3.5 2.5-5M6.5 20c.8-2 1.6-3.6 2-5.4M10 20c1-2 1.8-3.4 2.2-5" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M26 27c1-2 2-3.5 2.5-5M29.5 27c.8-2 1.6-3.4 2-5M23 27c.8-1.6 1.5-2.8 1.8-4" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 27.5h16M22 27.5h12" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" />
      <rect x="2" y="27.5" width="32" height="3.4" rx="1" fill={SOIL} opacity="0.28" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/tank-mix-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Pump sprayer tank with spray wand over lawn */}
      <path d="M8 14h12v13a2.5 2.5 0 01-2.5 2.5h-7A2.5 2.5 0 018 27z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#fff" />
      <rect x="9.5" y="18" width="9" height="8" rx="1" fill={G_LIGHT} opacity="0.7" />
      <path d="M10.5 11h7l1.5 3H9z" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M14 11V7" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M11 7h6" stroke={INK} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M20 17c3 0 5 1 6 3l2 4" stroke={INK} strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <path d="M28 24l1.8-2.2M29.2 25.6l2.4-1M29.4 27.4l2.6 0" stroke={WATER} strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="32.6" cy="21" r="0.8" fill={WATER} />
      <circle cx="33.4" cy="24.4" r="0.8" fill={WATER} />
      <path d="M3 31c0-3 1-5 2-6M6 31c0-4 1-6 2-7M24 31c0-3 1-5 2-6" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/fertilizer-rate-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Tilted fertilizer bag pouring granules, measuring scoop with LB badge */}
      <path d="M8 8l2-4h9l2 4v13a2.5 2.5 0 01-2.5 2.5h-8A2.5 2.5 0 018 21z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" transform="rotate(12 14.5 13)" />
      <rect x="10.8" y="10.5" width="8" height="5" rx="1" fill={G} transform="rotate(12 14.8 13)" />
      <text x="15" y="14.6" textAnchor="middle" fontSize="3.4" fontWeight="800" fill="#fff" fontFamily="ui-sans-serif, system-ui" transform="rotate(12 14.8 13)">NPK</text>
      <circle cx="23" cy="21" r="0.9" fill={G} />
      <circle cx="25.5" cy="24" r="0.9" fill={G_LIGHT} />
      <circle cx="23.5" cy="26.5" r="0.9" fill={G} />
      <circle cx="26.5" cy="28" r="0.7" fill={G_LIGHT} />
      <circle cx="28" cy="9" r="4.6" fill={SUN} stroke={INK} strokeWidth="1.2" />
      <text x="28" y="10.8" textAnchor="middle" fontSize="4.2" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">LB</text>
      <path d="M4 31c0-3 1-5 2-6M7 31c0-4 1-6 2-7M30 31c0-3 1-5 2-6M33 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/spreader-settings": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Broadcast hopper in three-quarter view with a gate lever and a dial arc,
          two wheels on the ground line, and a fan of granules spraying right.
          The dial ticks are decorative marks, never readable digits: the art
          must not assert a setting we have not sourced. */}
      <path d="M6 7h14l-2.4 9.6a2.4 2.4 0 01-2.33 1.82H10.7a2.4 2.4 0 01-2.33-1.82z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M5 7h16" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M11.5 18.4l1 4.4M16 18.4l-.6 4.4" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      {/* gate lever */}
      <path d="M17.6 13.2l4.6-2.6" stroke={G_DARK} strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="22.8" cy="10.2" r="1.5" fill={G} stroke={G_DARK} strokeWidth="0.9" />
      {/* dial arc with decorative ticks */}
      <path d="M24.6 17.4a5.4 5.4 0 00-5-3.3" stroke={GRAY} strokeWidth="1.2" strokeLinecap="round" />
      <path d="M20.4 13.2v-1.1M23 13.5l.6-1M25 15.6l1-.6M25.6 18h1.1" stroke={GRAY} strokeWidth="1" strokeLinecap="round" />
      {/* granule fan */}
      <circle cx="27" cy="20.6" r="0.9" fill={G} />
      <circle cx="30.2" cy="19.2" r="0.8" fill={G_LIGHT} />
      <circle cx="29.6" cy="23" r="0.9" fill={G} />
      <circle cx="32.6" cy="21.8" r="0.7" fill={G_LIGHT} />
      <circle cx="32.2" cy="25.4" r="0.7" fill={G} />
      {/* wheels + ground */}
      <circle cx="9.6" cy="26.6" r="3.4" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <circle cx="9.6" cy="26.6" r="1.1" fill={SOIL} />
      <circle cx="19.4" cy="26.6" r="3.4" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <circle cx="19.4" cy="26.6" r="1.1" fill={SOIL} />
      <path d="M3 31c0-3 1-5 2-6M28 31c0-3 1-5 2-6M32 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/sprinkler-cost-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Pop-up sprinkler head spraying, price tag */}
      <rect x="15" y="17" width="6" height="8" rx="1" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <rect x="16.5" y="13" width="3" height="4" rx="0.8" stroke={INK} strokeWidth="1.4" fill="#f0fdf4" />
      <path d="M18 12v-2" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M16 10c-2-2-4-3-7-3M20 10c2-2 4-3 7-3M17 9c-1-2.5-2-4-4-5.5M19 9c1-2.5 2-4 4-5.5" stroke={WATER} strokeWidth="1.3" strokeLinecap="round" fill="none" />
      <circle cx="7" cy="9" r="0.8" fill={WATER} />
      <circle cx="29" cy="9" r="0.8" fill={WATER} />
      <circle cx="12" cy="4.5" r="0.8" fill={WATER} />
      <circle cx="24" cy="4.5" r="0.8" fill={WATER} />
      <path d="M25 19h6l2 2.5-2 2.5h-6z" stroke={INK} strokeWidth="1.3" strokeLinejoin="round" fill={SUN} />
      <text x="28.4" y="23" textAnchor="middle" fontSize="4" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">$</text>
      <path d="M4 31c0-3 1-5 2-6M7 31c0-4 1-6 2-7M11 31c0-2 0-3 0-4M25 31c0-3 1-5 2-6M28 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/fertilizer-schedule-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Calendar with a sprout marking a feeding date */}
      <rect x="5" y="7" width="26" height="23" rx="2.6" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <rect x="5" y="7" width="26" height="6" rx="2.6" fill={G} />
      <path d="M11 5v4M25 5v4" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M9 18h3M15 18h3M21 18h3M27 18h1.5M9 23h3M21 23h3M27 23h1.5" stroke={GRAY} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="18" cy="23" r="3.4" fill="#f0fdf4" stroke={G} strokeWidth="1.2" />
      <path d="M18 24.6v-2.2M18 22.4c-.9 0-1.6-.6-1.6-1.4 1 0 1.6.6 1.6 1.4zM18 22.4c.9 0 1.6-.6 1.6-1.4-1 0-1.6.6-1.6 1.4z" stroke={G_DARK} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 33c0-2 1-3 2-3M31 33c0-2 1-3 2-3" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/lawn-cost-estimator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M9 4h18v26l-2.5-1.8L22 30l-2.5-1.8L17 30l-2.5-1.8L12 30l-3-1.8z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#fff" />
      <path d="M13 9h10M13 12h10M13 15h7" stroke={GRAY} strokeWidth="1.3" strokeLinecap="round" />
      <rect x="11" y="19" width="14" height="6" rx="1" fill={G_LIGHT} />
      <text x="18" y="23.5" textAnchor="middle" fontSize="5.6" fontWeight="800" fill={G_DARK} fontFamily="ui-sans-serif, system-ui">$</text>
      <circle cx="29" cy="11" r="3.5" fill={SUN} stroke={INK} strokeWidth="1.2" />
      <text x="29" y="13" textAnchor="middle" fontSize="3.6" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">$</text>
    </svg>
  ),
  "/tools/artificial-turf-cost-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* An unrolling roll of artificial turf over an aggregate base, with a $ badge */}
      <rect x="3" y="24" width="30" height="4" rx="1.4" fill="#fef3c7" stroke={INK} strokeWidth="1.2" />
      <circle cx="7" cy="26" r="0.7" fill={SOIL} />
      <circle cx="13" cy="26.4" r="0.6" fill={SOIL} />
      <circle cx="19" cy="26" r="0.6" fill={SOIL} />
      <path d="M4 24v-4h18l3 2-3 2z" fill={G_LIGHT} stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M6 20c0-3 0-4-1-5M9 20c0-3 0-4.5-.5-6M12 20c0-3 0-4-1-5M15 20c0-3 0-4.5-.5-6M18 20c0-3 0-4-1-5" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
      <ellipse cx="26" cy="20" rx="3.4" ry="4" fill="#f0fdf4" stroke={INK} strokeWidth="1.4" />
      <ellipse cx="26" cy="20" rx="1.3" ry="1.8" fill={G_LIGHT} stroke={G_DARK} strokeWidth="0.8" />
      <circle cx="29" cy="8" r="4" fill={SUN} stroke={INK} strokeWidth="1.2" />
      <text x="29" y="9.9" textAnchor="middle" fontSize="4.4" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">$</text>
    </svg>
  ),
  "/tools/topsoil-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Mound of topsoil with a shovel planted in it */}
      <path d="M3 28Q17 13 33 28Z" fill="#fef3c7" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="11" cy="24" r="0.9" fill={SOIL} />
      <circle cx="16" cy="22" r="0.9" fill={SOIL} />
      <circle cx="22" cy="24.5" r="0.8" fill={SOIL} />
      <circle cx="26" cy="26" r="0.7" fill={SOIL} />
      <circle cx="14" cy="26" r="0.7" fill={SOIL} />
      <g transform="rotate(26 20 17)">
        <path d="M17 6h6" stroke={INK} strokeWidth="1.8" strokeLinecap="round" />
        <rect x="19.2" y="6" width="1.6" height="13" rx="0.8" fill={INK} />
        <path d="M17 19h6v3.4c0 1.6-1.3 2.9-3 2.9s-3-1.3-3-2.9z" fill={G_LIGHT} stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
      </g>
      <path d="M3 31c0-2 1-3 2-4M6 31c0-3 1-4 2-5M28 31c0-2 1-3 2-4M31 31c0-3 1-4 2-5" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 32.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/grub-control-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* C-shaped white grub in a soil cross-section under grass, with a control check-badge */}
      <path d="M4 10c0-3 1-5 2-6M9 10c0-4 1-6 2-7M15 10c0-3 1-5 2-6M21 10c0-4 1-6 2-7M27 10c0-3 1-5 2-6M31 10c0-3 1-4 2-5" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M3 10h30" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M3 10h30v18a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill={SOIL} opacity="0.14" />
      <path d="M20 15.2A6.2 6.2 0 1 0 20 24.8L18 23.1A3.5 3.5 0 1 1 18 16.9Z" fill="#fefce8" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M11.4 17.6l1.8 0.8M10.9 20h2M11.4 22.4l1.8-0.8" stroke={INK} strokeWidth="0.8" strokeLinecap="round" opacity="0.55" />
      <circle cx="19.6" cy="15.8" r="1.5" fill={SOIL} stroke={INK} strokeWidth="0.7" />
      <path d="M17.5 17.6l-1.2 0.6M17.8 19.4l-1.3 0.2" stroke={INK} strokeWidth="0.8" strokeLinecap="round" />
      <circle cx="28.5" cy="26.5" r="3.5" fill="#fff" stroke={G_DARK} strokeWidth="1.4" />
      <path d="M26.8 26.5l1.1 1.1 2.2-2.3" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  "/tools/overseeding-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Broadcast spreader casting seed over an existing lawn */}
      <path d="M12 8h12l-2 7H14z" fill="#f0fdf4" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M13.5 8l1-2h7l1 2" stroke={INK} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <ellipse cx="18" cy="16.4" rx="5.2" ry="1.7" fill={G_LIGHT} stroke={INK} strokeWidth="1.2" />
      <circle cx="10" cy="21" r="0.9" fill={SOIL} />
      <circle cx="14" cy="22.5" r="0.9" fill={SOIL} />
      <circle cx="18" cy="23" r="0.9" fill={SOIL} />
      <circle cx="22" cy="22.5" r="0.9" fill={SOIL} />
      <circle cx="26" cy="21" r="0.9" fill={SOIL} />
      <circle cx="8" cy="23.5" r="0.7" fill={SOIL} />
      <circle cx="28" cy="23.5" r="0.7" fill={SOIL} />
      <path d="M4 31c0-3 1-4 2-5M7 31c0-3 1-5 2-6M11 31c0-2 1-3 2-4M15 31c0-3 1-4 2-5M19 31c0-3 1-5 2-6M22 31c0-2 1-3 2-4M25 31c0-2 1-3 2-4M29 31c0-3 1-5 2-6M32 31c0-3 1-4 2-5" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/sod-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Stacked pallet of sod slabs, grass over soil */}
      <rect x="6" y="24" width="24" height="5" rx="1" fill="#fef3c7" stroke={INK} strokeWidth="1.4" />
      <path d="M6 24h24" stroke={G} strokeWidth="1.8" strokeLinecap="round" />
      <rect x="8" y="18" width="20" height="5" rx="1" fill="#fef3c7" stroke={INK} strokeWidth="1.4" />
      <path d="M8 18h20" stroke={G} strokeWidth="1.8" strokeLinecap="round" />
      <rect x="10" y="12" width="16" height="5" rx="1" fill="#fef3c7" stroke={INK} strokeWidth="1.4" />
      <path d="M11.5 12c0-2 0-3-.5-4M14.5 12c0-2 0-3.5-.5-5M17.5 12c0-2 0-3-.5-4M20.5 12c0-2 0-3.5-.5-5M23.5 12c0-2 0-3-.5-4" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M2 31.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/weed-and-feed-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Feed granules (left) + a dandelion weed under control (right) over grass */}
      <circle cx="7" cy="7" r="1" fill={G} />
      <circle cx="11" cy="5.5" r="1" fill={G_LIGHT} />
      <circle cx="6" cy="11" r="0.8" fill={G_LIGHT} />
      <circle cx="10.5" cy="10" r="0.8" fill={G} />
      <circle cx="13.5" cy="8" r="0.7" fill={G_LIGHT} />
      <path d="M25 22v-8" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M25 17c-2.2-.6-3.8-.4-5.4.4M25 19c2.2-.6 3.8-.4 5.4.4" stroke={G_DARK} strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="25" cy="10" r="3.2" fill={SUN} stroke={INK} strokeWidth="1.1" />
      <path d="M25 6.8V5M25 13.2V15M21.8 10h-1.6M29.8 10h-1.6" stroke={SUN} strokeWidth="1.1" strokeLinecap="round" />
      <path d="M21 6l8 8" stroke={RED} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M4 31c0-3 1-5 2-6M7 31c0-4 1-6 2-7M11 31c0-2 1-3 2-4M15 31c0-3 1-5 2-6M29 31c0-3 1-5 2-6M32 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/lime-calculator": (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Soil-pH scale with a target marker over the healthy zone */}
      <rect x="5" y="14" width="4.5" height="6" fill="#f87171" stroke={INK} strokeWidth="0.8" />
      <rect x="9.5" y="14" width="4.5" height="6" fill="#fbbf24" stroke={INK} strokeWidth="0.8" />
      <rect x="14" y="14" width="4.5" height="6" fill="#a3e635" stroke={INK} strokeWidth="0.8" />
      <rect x="18.5" y="14" width="4.5" height="6" fill="#22c55e" stroke={INK} strokeWidth="0.8" />
      <rect x="23" y="14" width="4.5" height="6" fill="#3b82f6" stroke={INK} strokeWidth="0.8" />
      <rect x="27.5" y="14" width="4.5" height="6" fill="#7c3aed" stroke={INK} strokeWidth="0.8" />
      <path d="M20.75 11l2 2.6h-4z" fill={INK} />
      <text x="18.5" y="27" textAnchor="middle" fontSize="5" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">pH</text>
      <path d="M4 32c0-2 1-3 2-4M7 32c0-3 1-4 2-5M28 32c0-2 1-3 2-4M31 32c0-3 1-4 2-5" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  // --- Product-calculator family (shared bag, real NPK, identity badge) ---
  "/tools/milorganite-calculator": (
    <FertBag band={SOIL} label="6-4-0" badge={
      // organic leaf
      <>
        <path d="M14.4 25.4c1.6-2.8 4.6-3.2 6.8-2.6-.7 3.2-3.6 4.2-6.8 2.6z" fill={G} />
        <path d="M15 25c1.7-.7 3.6-1.1 5.2-1.1" stroke="#fff" strokeWidth="0.7" strokeLinecap="round" />
      </>
    } />
  ),
  "/tools/scotts-turf-builder-calculator": (
    <FertBag band={G} label="32-0-4" fontSize={3.2} badge={
      // grass tuft
      <path d="M15 26.6c0-2 0-3.4-.5-4.4M18 26.6c0-2.2 0-3.8-.5-5M21 26.6c0-2 0-3.4-.5-4.4" stroke={G_DARK} strokeWidth="1.3" strokeLinecap="round" />
    } />
  ),
  "/tools/scotts-weed-and-feed-calculator": (
    <FertBag band={G} label="28-0-3" fontSize={3.2} badge={
      // dandelion under control (red slash)
      <>
        <circle cx="18" cy="24.4" r="2.1" fill={SUN} stroke={INK} strokeWidth="0.7" />
        <path d="M18 22.1v-1.3" stroke={G_DARK} strokeWidth="0.9" strokeLinecap="round" />
        <path d="M15.2 27.2l5.6-5.6" stroke={RED} strokeWidth="1.3" strokeLinecap="round" />
      </>
    } />
  ),
  "/tools/scotts-triple-action-calculator": (
    <FertBag band={G} label="25-0-2" fontSize={3.2} badge={
      // three actions
      <>
        <circle cx="15" cy="24.6" r="1.1" fill={G} />
        <circle cx="18" cy="24.6" r="1.1" fill={G_DARK} />
        <circle cx="21" cy="24.6" r="1.1" fill={G} />
      </>
    } />
  ),
  "/tools/scotts-southern-lawn-food-calculator": (
    <FertBag band={G} label="32-0-10" fontSize={3} badge={
      // warm-season sun
      <>
        <circle cx="18" cy="24.4" r="1.9" fill={SUN} stroke={INK} strokeWidth="0.6" />
        <path d="M18 21.1v-1M18 28.7v-1M14.6 24.4h-1M22.4 24.4h-1M15.6 22l-.7-.7M21.1 26.8l-.7-.7M15.6 26.8l-.7.7M21.1 22l-.7.7" stroke={SUN} strokeWidth="0.8" strokeLinecap="round" />
      </>
    } />
  ),
  "/tools/pgf-complete-calculator": (
    <FertBag band={G} label="16-4-8" badge={
      // premium star
      <path d="M18 22l.9 1.9 2.1.3-1.5 1.4.4 2.1-1.9-1-1.9 1 .4-2.1-1.5-1.4 2.1-.3z" fill={SUN} stroke={INK} strokeWidth="0.5" strokeLinejoin="round" />
    } />
  ),
  "/tools/lesco-24-0-11-calculator": (
    <FertBag band={G_DARK} label="24-0-11" fontSize={3} badge={
      // pro-grade diamond
      <path d="M18 22.4l2.4 2.4L18 27.2 15.6 24.8z" fill="none" stroke={G_DARK} strokeWidth="1.2" strokeLinejoin="round" />
    } />
  ),
  "/tools/simple-lawn-solutions-calculator": (
    // Liquid concentrate — bottle instead of a bag.
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M4 31c0-3 1-5 2-6M7 31c0-4 1-6 2-7M27 31c0-3 1-5 2-6M30 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      <rect x="15.4" y="6" width="5.2" height="2.4" rx="0.6" stroke={INK} strokeWidth="1.3" fill="#fff" />
      <path d="M15 8.4h6v2.2l2 2v14a2.4 2.4 0 01-2.4 2.4h-5.2A2.4 2.4 0 0113 28.6v-14l2-2z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
      <path d="M13 20h10v8.6a2.4 2.4 0 01-2.4 2.4h-5.2A2.4 2.4 0 0113 28.6z" fill={WATER} opacity="0.22" />
      <rect x="13.9" y="14" width="8.2" height="4.6" rx="0.9" fill={G} />
      <text x="18" y="17.4" textAnchor="middle" fontSize="3.1" fontWeight="800" fill="#fff" fontFamily="ui-sans-serif, system-ui">16-4-8</text>
      <path d="M25 15c1 1.2 1.6 2.1 1.6 2.9a1.6 1.6 0 01-3.2 0c0-.8.6-1.7 1.6-2.9z" fill={WATER} />
    </svg>
  ),
  "/tools/ironite-calculator": (
    <FertBag band={G_DARK} label="Fe" fontSize={4.4} badge={
      // deep-green iron droplet
      <path d="M18 22c-1.5 1.7-2.3 3-2.3 4.1a2.3 2.3 0 004.6 0c0-1.1-.8-2.4-2.3-4.1z" fill={G_DARK} />
    } />
  ),
  "/tools/scotts-starter-fertilizer-calculator": (
    <FertBag band={G} label="24-25-4" fontSize={3} badge={
      // new-grass sprout + seeds
      <>
        <path d="M18 27v-2.6" stroke={G_DARK} strokeWidth="1" strokeLinecap="round" />
        <path d="M18 24.6c-1.1-.2-1.9-1-2.1-2.1 1.1.1 1.9.9 2.1 2.1zM18 24.6c1.1-.2 1.9-1 2.1-2.1-1.1.1-1.9.9-2.1 2.1z" fill={G} />
        <circle cx="14.8" cy="27" r="0.8" fill={SOIL} />
        <circle cx="21.2" cy="27" r="0.8" fill={SOIL} />
      </>
    } />
  ),
  "/tools/grass-comparison": (
    // Two grass blades side by side with a "vs" — the compare job, not a bag.
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M4 31c0-3 1-5 2-6M30 31c0-3 1-5 2-6" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M11 30c-2-7-1-13 1.5-18C15 17 15.5 24 13.5 30z" fill={G_LIGHT} stroke={G_DARK} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M25 30c-2-7-1-13 1.5-18C29 17 29.5 24 27.5 30z" fill="#bbf7d0" stroke={G_DARK} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="18" cy="19" r="4.4" fill="#fff" stroke={INK} strokeWidth="1.4" />
      <text x="18" y="21.1" textAnchor="middle" fontSize="4.6" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">vs</text>
    </svg>
  ),
  "/": (
    // Grass ID from a photo: a phone framing a grass tuft inside scan corners.
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M3 31c0-3 1-5 2-6M31 31c0-3 1-5 2-6" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      <rect x="9.5" y="3" width="17" height="28" rx="3" stroke={INK} strokeWidth="1.6" fill="#fff" />
      <rect x="11.5" y="6.5" width="13" height="19" rx="1.4" fill="#f0fdf4" />
      <path d="M14.5 23c0-3 .6-5 1.6-7M18 23c0-4 .4-7 .6-9.5M21.5 23c0-3-.6-5-1.6-7" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M13 11.5V9h2.5M21 9h2.5v2.5M23.5 20v2.5H21M15.5 22.5H13V20" stroke={G_DARK} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.5 28.4h3" stroke={GRAY} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
}

// Active tool routes without a bespoke illustration fall back to their manifest
// lucide glyph (resolved here, since the LucideIcon component can't cross the
// server→client boundary as a prop). Empty now that every active tool route has
// a bespoke illustration in TOOL_SVG; kept as the resolution seam for any future
// route that ships before its artwork does.
const FALLBACK_ICONS: Record<string, LucideIcon> = {}

// True when a route has a bespoke illustrated icon in TOOL_SVG (i.e. not just a
// lucide fallback). Lets client callers (e.g. the header Tools menu) decide
// whether to render the illustration or their own glyph for non-tool rows.
export function hasToolIllustration(href: string): boolean {
  return href in TOOL_SVG
}

export function ToolIllustration({ href }: { href: string }) {
  const svg = TOOL_SVG[href]
  if (svg) return <>{svg}</>

  const Fallback = FALLBACK_ICONS[href] ?? GitCompare
  return <Fallback className="h-7 w-7 text-green-700" strokeWidth={1.8} aria-hidden="true" />
}
